// src/components/PlannerActions.jsx
import React, { useRef, useState } from "react"
import { FaFileDownload, FaFileUpload, FaSpinner } from "react-icons/fa"

/**
 * Component for Export/Import functionality in the Planner
 *
 * @param {Object} props
 * @param {Object} props.productNodes - Current product nodes in the scene
 * @param {function} props.resetScene - Function to clear the scene
 * @param {React.RefObject} props.iframeRef - Reference to the Emersya iframe
 * @param {function} props.setLoadingProducts - Function to update loading state
 * @param {function} props.setProductNodes - Function to update productNodes state
 * @param {function} props.setProductNodesToAdd - Function to update productNodesToAdd state
 * @param {Object} props.nodeLocalIdsRef - Reference to track the current node ID counter
 * @param {string} props.floorMaterial - Current floor material
 * @param {function} props.setFloorMaterial - Function to set floor material
 */
const PlannerActions = ({
  productNodes,
  resetScene,
  iframeRef,
  setLoadingProducts,
  setProductNodes,
  setProductNodesToAdd,
  nodeLocalIdsRef,
  floorMaterial,
  setFloorMaterial,
  plannerPlayerRef,
}) => {
  const fileInputRef = useRef(null)
  const [isImporting, setIsImporting] = useState(false)
  const [isExporting, setIsExporting] = useState(false)

  /**
   * Get current camera position from Emersya
   * @returns {Promise<Object|null>} Camera data or null
   */
  const getCameraData = () => {
    return new Promise((resolve) => {
      if (!iframeRef.current || !iframeRef.current.contentWindow) {
        console.warn("[EXPORT] Iframe not available for camera data")
        resolve(null)
        return
      }

      let hasResolved = false

      const messageHandler = (event) => {
        const data = event.data
        if (data && data.action === "onGetCamera" && data.camera && !hasResolved) {
          hasResolved = true
          window.removeEventListener("message", messageHandler)

          resolve({
            position: Array.from(data.camera.position),
            target: Array.from(data.camera.target),
            up: Array.from(data.camera.up),
            fov: data.camera.fov,
          })
        }
      }

      window.addEventListener("message", messageHandler)

      // Request camera data
      iframeRef.current.contentWindow.postMessage(
        { action: "getCamera" },
        "*"
      )

      // Timeout after 3 seconds
      setTimeout(() => {
        if (!hasResolved) {
          hasResolved = true
          window.removeEventListener("message", messageHandler)
          console.warn("[EXPORT] Camera data timeout")
          resolve(null)
        }
      }, 3000)
    })
  }

  /**
   * Set camera position in Emersya
   * @param {Object} cameraData - Camera position data
   */
  const setCameraPosition = (cameraData) => {
    if (!iframeRef.current || !iframeRef.current.contentWindow || !cameraData) {
      return
    }

    iframeRef.current.contentWindow.postMessage(
      {
        action: "setCamera",
        position: cameraData.position,
        target: cameraData.target,
        up: cameraData.up || [0, 1, 0],
        fov: cameraData.fov || 45,
        transitionTime: 1000,
      },
      "*"
    )

    console.log("[IMPORT] Camera position restored:", cameraData)
  }

  /**
   * Set floor material in Emersya - uses the PlannerPlayer's exposed function
   * @param {string} material - Floor material name
   */
  const setFloorMaterialInViewer = (material) => {
    if (!material) {
      return
    }

    console.log("[IMPORT] Setting floor material via PlannerPlayer:", material)

    // Use the PlannerPlayer's exposed function - same code as toggle button
    if (plannerPlayerRef?.current?.setFloorMaterialDirect) {
      plannerPlayerRef.current.setFloorMaterialDirect(material)
    } else {
      console.warn("[IMPORT] PlannerPlayer ref not available, using fallback")
      // Fallback to direct postMessage if ref not available
      if (iframeRef.current?.contentWindow) {
        iframeRef.current.contentWindow.postMessage(
          {
            action: "setMaterials",
            materials: [
              {
                materialVariation: material,
                configurableMaterial: "DefaultMaterial",
              },
            ],
          },
          "*"
        )
      }
      if (setFloorMaterial) {
        setFloorMaterial(material)
      }
    }
  }

  /**
   * Exports the current scene to a JSON file
   */
  const exportScene = async () => {
    if (Object.keys(productNodes).length === 0) {
      alert("Nessun prodotto da esportare / No products to export")
      return
    }

    setIsExporting(true)

    try {
      // Get camera data
      const cameraData = await getCameraData()

      // Create a JSON-serializable version of productNodes
      const exportData = {
        version: "1.1", // Updated version for new format
        timestamp: new Date().toISOString(),
        // Scene settings
        scene: {
          floorMaterial: floorMaterial || "piano",
          camera: cameraData,
        },
        products: Object.values(productNodes).map((node) => {
          // Convert Float32Array to regular array for position
          const position =
            node.position instanceof Float32Array
              ? Array.from(node.position)
              : node.position

          // Extract applied material variations
          const configurableMaterials = []
          if (
            node.configurableMaterials &&
            Array.isArray(node.configurableMaterials)
          ) {
            node.configurableMaterials.forEach((mat) => {
              if (mat.name) {
                configurableMaterials.push({
                  name: mat.name,
                  appliedVariation: mat.appliedVariation || null,
                })
              }
            })
          }

          // Save important reference data for restoring the preview
          const previewPath = node.refData?.previewPath || null
          const name =
            node.refData?.name || node.matchedLocalProduct?.name || node.SKU

          return {
            SKU: node.SKU,
            projectCode: node.projectCode,
            name: name,
            previewPath: previewPath,
            position,
            rotationAngle: node.rotationAngle || 0,
            height: node.height || 1,
            configurableMaterials,
          }
        }),
      }

      // Convert to JSON and create blob
      const jsonString = JSON.stringify(exportData, null, 2)
      const blob = new Blob([jsonString], { type: "application/json" })

      // Create download link and trigger download
      const url = URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      link.download = `planner-scene-${new Date()
        .toISOString()
        .slice(0, 10)}.json`
      document.body.appendChild(link)
      link.click()

      // Clean up
      document.body.removeChild(link)
      URL.revokeObjectURL(url)

      console.log(
        "[EXPORT] Scene exported successfully with",
        exportData.products.length,
        "products, floor:",
        exportData.scene.floorMaterial,
        ", camera:",
        cameraData ? "saved" : "not saved"
      )
    } catch (error) {
      console.error("[EXPORT ERROR]", error)
      alert(
        `Errore durante l'esportazione: ${error.message} / Export error: ${error.message}`
      )
    } finally {
      setIsExporting(false)
    }
  }

  /**
   * Triggers the file upload dialog
   */
  const triggerFileUpload = () => {
    fileInputRef.current.click()
  }

  /**
   * Apply materials sequentially with short delays (150ms for speed)
   * @param {Array} materialsToApply - Array of material info objects
   * @param {string} presetFloorMaterial - Floor material to re-apply at end (safety measure)
   */
  const applyMaterialsSequentially = async (materialsToApply, presetFloorMaterial) => {
    console.log(`[IMPORT] Applying ${materialsToApply.length} materials`)

    for (let i = 0; i < materialsToApply.length; i++) {
      const { localId, configurableMaterial, materialVariation } = materialsToApply[i]

      // Update state
      setProductNodes((prev) => {
        const node = prev[localId]
        if (!node || !node.configurableMaterials) return prev

        const updatedMaterials = node.configurableMaterials.map((material) => {
          if (material.name === configurableMaterial) {
            return {
              ...material,
              appliedVariation: materialVariation,
              activeVariation: materialVariation,
            }
          }
          return material
        })

        return {
          ...prev,
          [localId]: {
            ...node,
            configurableMaterials: updatedMaterials,
          },
        }
      })

      // Send to Emersya - one material at a time
      try {
        iframeRef.current.contentWindow.postMessage(
          {
            action: "setMaterials",
            materials: [
              {
                configurableMaterial,
                materialVariation,
                nodeLocalIds: [localId],
              },
            ],
          },
          "*"
        )
      } catch (error) {
        console.error("[IMPORT] Error applying material:", error)
      }

      // Fast delay between materials (150ms for speed)
      if (i < materialsToApply.length - 1) {
        await new Promise((resolve) => setTimeout(resolve, 150))
      }
    }

    console.log("[IMPORT] All materials applied, waiting for Emersya to finish processing...")

    // Re-apply floor material at the end as safety
    if (presetFloorMaterial) {
      await new Promise((resolve) => setTimeout(resolve, 4000))
      console.log("[IMPORT] Applying floor material:", presetFloorMaterial)
      plannerPlayerRef?.current?.setFloorMaterialDirect(presetFloorMaterial)
    }

    setIsImporting(false)
  }

  /**
   * Handles the file import process
   * @param {Event} event - The file input change event
   */
  const handleFileImport = (event) => {
    const file = event.target.files[0]
    if (!file) return

    setIsImporting(true)

    // Read the file directly here and pass to Planner component
    const reader = new FileReader()
    reader.onload = async (e) => {
      try {
        const jsonData = JSON.parse(e.target.result)

        // Basic validation
        if (!jsonData.products || !Array.isArray(jsonData.products)) {
          throw new Error("Invalid file format: missing products array")
        }

        console.log(
          "[IMPORT] Importing scene with",
          jsonData.products.length,
          "products"
        )

        // Clear current scene before importing
        resetScene()

        // Store scene settings for later application
        const savedSceneSettings = jsonData.scene

        // STEP 1: Apply floor material FIRST (3 seconds) to stop rotation
        if (savedSceneSettings?.floorMaterial) {
          setTimeout(() => {
            setFloorMaterialInViewer(savedSceneSettings.floorMaterial)
          }, 3000)
        }

        // STEP 2: Apply camera SECOND (4 seconds) to set the view
        if (savedSceneSettings?.camera) {
          setTimeout(() => {
            setCameraPosition(savedSceneSettings.camera)
          }, 4000)
        }

        // Store the starting localId for reference
        const startingLocalId = nodeLocalIdsRef.current + 1

        // Add each product to the scene
        jsonData.products.forEach((product, index) => {
          // Generate a new local ID for this product
          const localId = startingLocalId + index

          // Set loading state
          setLoadingProducts((prev) => ({
            ...prev,
            [localId]: true,
          }))

          // Prepare the node for adding to the scene
          const node = {
            SKU: product.SKU,
            projectCode: product.projectCode,
            localId,
            parentLocalId: 1,
            draggable: true,
            tracked: true,
            position: product.position,
            rotationAxis: [0, 1, 0],
            rotationAngle: product.rotationAngle || 0,
            configurableMaterials: [],
            height: product.height || 1,
          }

          // Add to productNodesToAdd queue with proper refData
          setProductNodesToAdd((prev) => ({
            ...prev,
            [localId]: {
              ...node,
              height: product.height || 1,
              refData: {
                name: product.name || product.SKU,
                projectCode: product.projectCode,
                // Use the CDN URL pattern to recreate the preview path if it's not saved
                previewPath:
                  product.previewPath ||
                  `https://cdn.emersya.com/cl/3IB82Q24UW/p/${
                    product.projectCode
                  }/productNodes/86003?date=${Date.now()}`,
              },
              // Set applied material variations as default variations
              configurableMaterials: Array.isArray(
                product.configurableMaterials
              )
                ? product.configurableMaterials.map((mat) => ({
                    ...mat,
                    activeVariation: mat.appliedVariation, // Make sure activeVariation is set
                  }))
                : [],
            },
          }))

          // Send message to add product to Emersya
          iframeRef.current.contentWindow.postMessage(
            {
              action: "addProductNodesInstances",
              id: localId,
              nodes: [node],
            },
            "*"
          )
        })

        // Update the nodeLocalIdsRef to prevent ID collisions
        nodeLocalIdsRef.current =
          startingLocalId + jsonData.products.length - 1

        // Create a queue to apply materials after products are loaded
        const materialsToApply = []

        // Collect all materials that need to be applied
        jsonData.products.forEach((product, index) => {
          const localId = startingLocalId + index

          if (
            product.configurableMaterials &&
            Array.isArray(product.configurableMaterials)
          ) {
            product.configurableMaterials.forEach((material) => {
              if (material.name && material.appliedVariation) {
                materialsToApply.push({
                  localId,
                  configurableMaterial: material.name,
                  materialVariation: material.appliedVariation,
                })
              }
            })
          }
        })

        // STEP 3: Apply materials LAST (8 seconds) with faster delays
        if (materialsToApply.length > 0) {
          console.log(
            "[IMPORT] Will apply",
            materialsToApply.length,
            "materials after floor/camera"
          )

          // Apply at 8 seconds (after floor at 3s and camera at 4s)
          setTimeout(() => {
            applyMaterialsSequentially(materialsToApply, savedSceneSettings?.floorMaterial)
          }, 8000)
        } else {
          // If no materials to apply, finish import process
          setTimeout(() => {
            setIsImporting(false)
          }, 5000)
        }

        // Reset file input
        event.target.value = null
      } catch (error) {
        console.error("[IMPORT ERROR]", error)
        alert(
          `Errore durante l'importazione: ${error.message} / Import error: ${error.message}`
        )
        event.target.value = null
        setIsImporting(false)
      }
    }

    reader.onerror = () => {
      console.error("[IMPORT ERROR] Failed to read file")
      alert("Errore nella lettura del file / Error reading file")
      event.target.value = null
      setIsImporting(false)
    }

    reader.readAsText(file)
  }

  return (
    <div className="ay-flex ay-gap-2 ay-mb-4">
      <button
        onClick={exportScene}
        disabled={isExporting || Object.keys(productNodes).length === 0}
        className="ay-flex-1 ay-flex ay-items-center ay-justify-center ay-gap-2 ay-bg-gray-200 ay-text-gray-700 ay-px-3 ay-py-2 ay-rounded ay-text-xs ay-font-medium hover:ay-bg-gray-300 ay-transition-colors disabled:ay-opacity-50 disabled:ay-cursor-not-allowed"
        title="Esporta scena / Export scene"
      >
        {isExporting ? (
          <FaSpinner className="ay-animate-spin" size={12} />
        ) : (
          <FaFileDownload size={12} />
        )}
        <span>ESPORTA</span>
      </button>

      <button
        onClick={triggerFileUpload}
        disabled={isImporting}
        className="ay-flex-1 ay-flex ay-items-center ay-justify-center ay-gap-2 ay-bg-gray-200 ay-text-gray-700 ay-px-3 ay-py-2 ay-rounded ay-text-xs ay-font-medium hover:ay-bg-gray-300 ay-transition-colors disabled:ay-opacity-50 disabled:ay-cursor-not-allowed"
        title="Importa scena / Import scene"
      >
        {isImporting ? (
          <FaSpinner className="ay-animate-spin" size={12} />
        ) : (
          <FaFileUpload size={12} />
        )}
        <span>IMPORTA</span>
      </button>

      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileImport}
        accept=".json"
        className="ay-hidden"
      />
    </div>
  )
}

export default PlannerActions
